/**
 * OneTrust Gate Handler
 *
 * WILL BE SIMPLIFIED DURING PRODUCTIZATION (~2 weeks post-rollout)
 *
 * Current purpose (during rollout):
 * - Track Statsig gate value for CM selection
 * - Provide isOneTrustAnalyticsStub() utility for stub detection
 *
 * After productization:
 * - Remove gate tracking (setGateValue, getGateValue, isGateKnown)
 * - Simplify to utility class with only isOneTrustAnalyticsStub()
 * - Or inline isOneTrustAnalyticsStub() into router and delete this file
 *
 * No queuing - events check analytics readiness on each call.
 */

// Match e2e/util.ts Window.analytics so declaration merging succeeds
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SegmentMethod = (...args: any[]) => void;

declare global {
  interface Window {
    analytics?: {
      _isOneTrustStub?: boolean;
      track?: SegmentMethod;
      page?: SegmentMethod;
      identify?: SegmentMethod;
    };
  }
}

export class OneTrustGateHandler {
  private isOneTrustGateEnabled: boolean | null = null;

  /**
   * Check if the gate value is known
   */
  isGateKnown(): boolean {
    return this.isOneTrustGateEnabled !== null;
  }

  /**
   * Get the current gate value (false if unknown)
   */
  getGateValue(): boolean {
    return this.isOneTrustGateEnabled ?? false;
  }

  /**
   * Update the gate value from Statsig
   * No longer returns queued events - queueing is removed
   */
  setGateValue(enabled: boolean): void {
    this.isOneTrustGateEnabled = enabled;
  }

  /**
   * Check if OneTrust analytics is still in stub mode (user hasn't consented yet)
   *
   * Returns true when:
   * - OneTrust is enabled AND
   * - window.analytics exists but is still the stub (not real analytics.js)
   *
   * Returns false when:
   * - Gate is unknown (legacy check will handle)
   * - Gate is false (legacy consent manager)
   * - OneTrust with real analytics loaded (user has consented)
   */
  isOneTrustAnalyticsStub(): boolean {
    // Gate unknown or legacy - no stub check needed
    if (this.isOneTrustGateEnabled !== true) {
      return false;
    }

    // OneTrust enabled - check if real analytics.js has loaded
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return !!(window.analytics && window.analytics._isOneTrustStub);
  }
}
